import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { companyBillingSitesPath, Color, EDIT_FIELDS, NEW_DOCUMENT_ID, QUOTE_EXPIRY_DAYS, Size, addCalendarDays, lineDiscountOf, t, type DividerRow, type QuoteLineInput, newDocumentTitle } from '@bms2/shared';
import { Button, DatePicker, Dropdown, NumericInput, Select, TextInput } from '../../components/controls';
import { ItemTable, hasUnnamedDivider, newLine, savedRows, withDividers, withGeneratedLines, type ItemLineDraft } from '../../components/composites/ItemTable';
import { ModalField } from '../../components/modals/ModalShell';
import { useDocumentToday } from '../../shell/ViewerZone';
import { CustomerNotesBox } from './CustomerNotesBox';
import { DocumentEditPage, documentEditStyles as styles } from './DocumentEditPage';
import { QUOTES_PATH } from './QuotePanels';
import { ShipmentPreferencePicker } from './ShipmentPreferencePicker';
import { SitePicker, useSiteChoices } from './SitePicker';
import { apiFetch, get, post, postingAccountSearch, put, taxCategorySearch, useApi } from './api';
import { useCompanySearch, useItemPicker } from '../settings/api';
import { previewLinesOf, useTaxPreview } from './useTaxPreview';
import { useAddNew } from './AddNewHost';
import { QuoteRecipients, type RecipientChoice } from './QuoteRecipients';

/** What the form opens on: the Account's sites with the two the rules would pick, its terms, and the stored header where one exists. */
export interface QuoteDefaults {
  companyId: string;
  companyName: string;
  paymentTerms: string;
  sites: { id: string; label: string }[];
  billToSiteId: string | null;
  shipToSiteId: string | null;
  billToVia: string;
  /** 🔒 The Deal, with its type and its Not to Exceed, the ceiling a Service quote reads and writes (decision 2612); both absent from an older reply. */
  deal: { id: string; name: string; stage: string; agreementId: string | null; dealType?: string; notToExceedCents?: number | null } | null;
  taxExempt: boolean;
  /** 🔒 The Discount Scope setting, so the Discount column follows it rather than a literal (decisions 0097, 0651); absent from an older reply. */
  discountLevel?: string;
  quoteId?: string | null;
  quoteNumber?: string | null;
  quoteDate?: string | null;
  expiryDate?: string | null;
  displayMode?: string | null;
  /** 🔒 How the goods travel (decision 0682); absent on a create and from an older reply. */
  shipmentPreferenceId?: string | null;
  /** 🔒 The notes to the customer Region 7 prints (decision 0683); absent on a create and from an older reply. */
  customerNotes?: string | null;
  /** 🔒 The customer's own purchase order number (T1720; decision 2290); absent on a create and from an older reply. */
  customerPoNumber?: string | null;
  /** 🔒 The lines a Quote raised on a Deal opens on, by the Deal's type (decision 1628); absent where there are none. */
  prefill?: QuotePrefillRow[];
  /** 🔒 The Account's contacts as tick boxes, the Deal's primary contact ticked (T922; decision 1750); absent from an older reply. */
  recipients?: RecipientChoice[];
}

/** 🔒 One line the create opens on (decision 1628): a Generate draft, or a Project's scope line over one band at its floor. */
interface QuotePrefillRow {
  itemId: string | null;
  sku: string | null;
  itemName: string;
  description: string;
  quantity: number;
  unitId: string | null;
  unitName: string;
  unitPriceCents: number;
  bomLineIds: string[];
  budgetedHours?: number | null;
  isService?: boolean;
}

/** One BOM line the Deal offers as a starting point for a Quote line, priced for the customer and never at cost (decision 0440). */
interface BomLineDraftRow {
  bomLineId: string;
  itemId: string;
  sku: string;
  itemName: string;
  description: string;
  quantity: number;
  unitId: string;
  unitName: string;
  unitPriceCents: number;
  /** 🔒 The BOM line's budget in hours and whether it is a Service (decision 0860); absent from an older reply. */
  budgetedHours?: number | null;
  isService?: boolean;
}

const displayModes = EDIT_FIELDS.Quote.find((field) => field.key === 'displayMode')?.options ?? [];

/**
 * 🔒 The Quote's edit mode (decision 0409), on the Quote's own page rather than
 * in a modal. The Account is the record the page was opened from where one
 * supplied it; opened from the Quotes list it is asked for, and the defaults are
 * read again once it is chosen. The bill-to and the ship-to open on what the
 * auto-population rules resolve, and both stay editable. The lines follow on the
 * Item Table, and the sticky footer carries Save as Draft, Save and Send and
 * Cancel.
 *
 * 💰 A line added before the Quote exists seeds its rate from the Item's own
 * sales price rather than the Account's price sheet, because the price-sheet
 * read is keyed on a Quote that has not been written yet. The saved Quote's own
 * Lines tile applies the sheet from then on.
 */
export function QuoteForm({ quoteId = null, apiBaseUrl = '' }: { quoteId?: string | null; apiBaseUrl?: string }) {
  const addNew = useAddNew(apiBaseUrl);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const dealFromUrl = params.get('dealId');
  const companyFromUrl = params.get('companyId');
  /** The Account picked here where the entry point supplied none; the list's + New is the one that does. */
  const [account, setAccount] = useState<{ id: string; label: string } | null>(null);
  const companyId = companyFromUrl || account?.id || '';
  const query = quoteId
    ? `quoteId=${encodeURIComponent(quoteId)}`
    : dealFromUrl
    ? `dealId=${encodeURIComponent(dealFromUrl)}`
    : companyId
    ? `companyId=${encodeURIComponent(companyId)}`
    : null;
  // 🔨 No Account, nothing to read: the form asks for one rather than reading defaults for the empty string and failing.
  const defaults = useApi<QuoteDefaults>(query ? `${QUOTES_PATH}/defaults?${query}` : null, apiBaseUrl);
  const answer = defaults.state.kind === 'ready' ? defaults.state.data : null;
  // 🔒 The account's sites, read again once + Add New Site writes one (T185).
  const siteChoices = useSiteChoices(answer, answer ? companyBillingSitesPath(answer.companyId) : null, apiBaseUrl);
  const siteAdded = siteChoices.added;
  const needsAccount = query === null;
  /** The entry point supplied no Account, so the picker stands on the form and stays changeable. */
  const asksForAccount = !quoteId && !dealFromUrl && !companyFromUrl;
  const readFailed = defaults.state.kind === 'failed';
  /**
   * 🔨 The reopening read carries the stored category, account and discount
   * (decisions 0097, 0435). 💰 It carried none of the three, so an edit through
   * this form reset every line's category to the Item's and billed the list
   * price (found while building, 2026-09-16).
   *
   * 🔨 It carries `bomLineIds` too, or a reopened Draft holds an empty skip set
   * and a second Generate appends every BOM line again (found in review,
   * 2026-09-16; decision 0440).
   */
  const storedLines = useApi<{ rows: { id: string; itemId: string | null; itemLabel: string; sku: string | null; description: string; quantity: number; unitName: string; unitPriceCents: number; bomLineIds: string[]; discountType: string | null; discountValue: number | null; taxCategoryId: string; taxCategoryName: string; accountId: string | null; accountLabel: string | null; budgetedHours?: number | null; isService?: boolean; isAllowance?: boolean; isOptional?: boolean }[]; dividers?: DividerRow[] }>(
    quoteId ? `${QUOTES_PATH}/${quoteId}/lines` : null,
    apiBaseUrl,
  );

  const [billTo, setBillTo] = useState<string | null>(null);
  const [shipTo, setShipTo] = useState<string | null>(null);
  const [terms, setTerms] = useState('');
  /**
   * 🔨 The two dates open on today and today plus thirty days, which is what
   * piece 22 says the form does. 💰 They opened empty, so the person typed what
   * the server would have defaulted and the expiry the Expired derivation reads
   * was whatever they typed (found in review, 2026-09-15). A stored Quote's own
   * dates replace both once the read lands.
   */
  // 🔒 Today in the viewer's own zone, read through the one hook every document form reads (decision 1030).
  const today = useDocumentToday();
  const [quoteDate, setQuoteDate] = useState(today);
  const [expiryDate, setExpiryDate] = useState(() => addCalendarDays(today, QUOTE_EXPIRY_DAYS));
  const [displayMode, setDisplayMode] = useState('Parts');
  const [shipmentPreference, setShipmentPreference] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [customerPo, setCustomerPo] = useState('');
  // 🔒 A Service deal's Not to Exceed, the Deal's own ceiling (decision 2612).
  const [notToExceed, setNotToExceed] = useState<number | null>(null);
  // 🔨 The figure the form opened on: a save sends the Not to Exceed only when it moved, so a stale form never reverts a raise made on another quote of the Deal (T1821).
  const [openedNotToExceed, setOpenedNotToExceed] = useState<number | null>(null);
  // 🔒 Who the Quote goes to (T922; decision 1750): the contacts the Account lists, and the ones ticked.
  const [contacts, setContacts] = useState<RecipientChoice[]>([]);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  // 🔒 A fresh document opens on one empty line, so the table is typed into rather than found (decision 0447).
  const [drafts, setDrafts] = useState<ItemLineDraft[]>(() => [newLine()]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 🔒 A picked Item brings its SKU and its sales description, as the purchase side's lines always did (T121; piece 4, the SKU under the name; decision 0303).
  // 🔒 Which picked Items are Services, which draws the Budget (hours) cell (decision 0860).

  // The defaults arriving after the form opened fill the fields nobody has touched.
  const [seen, setSeen] = useState<QuoteDefaults | null>(null);
  const [prefilled, setPrefilled] = useState(false);
  if (answer && seen !== answer) {
    setSeen(answer);
    setBillTo(answer.billToSiteId);
    setShipTo(answer.shipToSiteId);
    setTerms(answer.paymentTerms);
    if (answer.quoteDate) setQuoteDate(answer.quoteDate);
    if (answer.expiryDate) setExpiryDate(answer.expiryDate);
    if (answer.displayMode) setDisplayMode(answer.displayMode);
    setShipmentPreference(answer.shipmentPreferenceId ?? '');
    setCustomerNotes(answer.customerNotes ?? '');
    setCustomerPo(answer.customerPoNumber ?? '');
    setNotToExceed(answer.deal?.notToExceedCents ?? null);
    setOpenedNotToExceed(answer.deal?.notToExceedCents ?? null);
    setContacts(answer.recipients ?? []);
    setTicked(new Set((answer.recipients ?? []).filter((c) => c.ticked).map((c) => c.personId)));
    /*
     * 🔒 A Quote raised on a Deal opens on its lines already filled (decision
     * 1628), once: a later read of the same defaults, such as + Add New Site's,
     * never discards what the person has typed since.
     */
    if (!quoteId && !prefilled && answer.prefill && answer.prefill.length > 0) {
      setPrefilled(true);
      setDrafts(withGeneratedLines([newLine()], answer.prefill.map((row, index) => ({
        // 🔨 The id opens on the new-line prefix, so the save writes these as new lines.
        id: `line-prefill-${index}`,
        itemId: row.itemId,
        // A free-text line saves its label as its description, so the band's name stands there.
        itemLabel: row.itemId === null ? row.description : row.itemName,
        sku: row.sku,
        description: row.description,
        qty: row.quantity,
        unit: row.unitName,
        unitId: row.unitId,
        rateCents: row.unitPriceCents,
        bomLineIds: [...row.bomLineIds],
        service: row.isService === true,
        budgetedHours: row.budgetedHours ?? null,
        freeText: row.itemId === null,
      }))));
    }
  }

  const rows = storedLines.state.kind === 'ready' ? storedLines.state.data.rows : null;
  const storedDividers = storedLines.state.kind === 'ready' ? storedLines.state.data.dividers : null;
  const [seenLines, setSeenLines] = useState<unknown>(null);
  if (rows && seenLines !== rows) {
    setSeenLines(rows);
    // 🔒 The stored group dividers stand among the lines where they were saved (decision 0650).
    setDrafts(withDividers(rows.map((row) => {
      const discount = lineDiscountOf(row.discountType, row.discountValue);
      return {
        id: row.id, itemId: row.itemId, itemLabel: row.itemLabel, sku: row.sku, description: row.description, qty: row.quantity, unit: row.unitName, rateCents: row.unitPriceCents,
        bomLineIds: row.bomLineIds,
        service: row.isService === true, budgetedHours: row.budgetedHours ?? null,
        allowance: row.isAllowance === true,
        optional: row.isOptional === true,
        accountId: row.accountId, accountLabel: row.accountLabel ?? '', taxCategoryId: row.taxCategoryId, taxLabel: row.taxCategoryName,
        ...(discount ? { discount } : {}),
        freeText: row.itemId === null,
      };
    }), storedDividers));
  }
  // 🔒 A Service deal's quote is the one that takes a Not to Exceed and allowance lines (decision 2612).
  const isService = answer?.deal?.dealType === 'Service';
  const accountSearch = useCompanySearch(apiBaseUrl, 'customer', { activeOnly: true });
  const { unitIds, pickerProps } = useItemPicker(apiBaseUrl, 'sales');

  /**
   * Generate Quote lines from the BOM (decision 0440): the Deal's list as a
   * one-click starting point, and the result is the rep's to edit. 🔨 It
   * appends rather than replaces, and a BOM line the grid already names is
   * skipped, so a second press cannot double a Quote's lines.
   */
  const dealForBom = answer?.deal?.id ?? dealFromUrl ?? null;
  const [generating, setGenerating] = useState(false);
  const generateFromBom = async (): Promise<void> => {
    if (!dealForBom) return;
    setGenerating(true);
    const response = await apiFetch(`${apiBaseUrl}${QUOTES_PATH}/bom-lines?dealId=${encodeURIComponent(dealForBom)}`).catch(/* silent: a fetch the server never answered cannot reach the server's log either; the page says it could not reach the server. */ () => null);
    setGenerating(false);
    if (!response || !response.ok) return setError(t('quote.new.bomFailed'));
    const body = (await response.json()) as { rows: BomLineDraftRow[] };
    const already = new Set(drafts.flatMap((d) => d.bomLineIds ?? []));
    const fresh = body.rows.filter((row) => !already.has(row.bomLineId));
    if (fresh.length === 0) return setError(body.rows.length === 0 ? t('quote.new.bomEmpty') : t('quote.new.bomAlready'));
    setDrafts(withGeneratedLines(drafts, [
      // 🔨 The id opens on the new-line prefix, so the save writes these as new lines rather than hunting stored rows under the BOM's identities.
      ...fresh.map((row) => ({
        id: `line-bom-${row.bomLineId}`,
        itemId: row.itemId,
        itemLabel: row.itemName,
        sku: row.sku,
        description: row.description,
        qty: row.quantity,
        unit: row.unitName,
        unitId: row.unitId,
        rateCents: row.unitPriceCents,
        bomLineIds: [row.bomLineId],
        // 🔒 The BOM line's budget comes with it (decision 0860).
        service: row.isService === true,
        budgetedHours: row.budgetedHours ?? null,
        freeText: false,
      })),
    ]));
  };

  const taxSearch = useMemo(() => taxCategorySearch(apiBaseUrl), [apiBaseUrl]);
  // 🔒 The GL account a line posts to; the header's own picker is the customer Account and the two are never one control (decision 0435).
  const lineAccountSearch = useMemo(() => postingAccountSearch(apiBaseUrl), [apiBaseUrl]);

  // 🔒 The lines the save keeps, and the group dividers at their places among them (decision 0650).
  const saveRows = () =>
    savedRows<QuoteLineInput>(
      drafts,
      (d) => Boolean(d.itemId || d.itemLabel.trim() || d.description.trim()),
      (d) => ({
        ...(d.id && !d.id.startsWith('line-') ? { id: d.id } : {}),
        itemId: d.itemId,
        description: (d.freeText ? d.itemLabel : d.description).trim() || null,
        quantity: d.qty ?? 1,
        // The unit the grid's closed list wrote wins over the Item's own, which is what an override means (decision 0439).
        unitId: d.unitId ?? (d.itemId ? unitIds.current.get(d.itemId) ?? null : null),
        unitPriceCents: d.rateCents ?? null,
        // 🔨 The discount the line opened on goes back with it; a save that drops it bills the list price (decision 0097).
        discountType: d.discount ? d.discount.kind : null,
        discountValue: d.discount ? (d.discount.kind === 'Percentage' ? d.discount.percent : d.discount.amount.amount / 100) : null,
        taxCategoryId: d.taxCategoryId ?? null,
        accountId: d.accountId ?? null,
        // The BOM rows the line rolls up, which is what makes its margin floor computable (decision 0243).
        ...(d.bomLineIds && d.bomLineIds.length > 0 ? { bomLineIds: [...d.bomLineIds] } : {}),
        // 🔒 A service line's budget in hours; any other line sends none (decision 0860).
        budgetedHours: d.service ? d.budgetedHours ?? null : null,
        // 🔒 The Allowance mark, on a Service deal's quote alone (decision 2612).
        ...(isService && d.allowance ? { isAllowance: true } : {}),
        // 🔒 A line the customer may take or leave (decision 2650).
        ...(d.optional ? { isOptional: true } : {}),
      }),
    );

  // 🔒 The tax is worked out live, through the call the save makes, on every change to a line, the Ship-To or the date (decision 0644).
  const preview = useTaxPreview(
    answer
      ? {
          documentType: 'Quote', documentId: quoteId, shipToSiteId: shipTo || null, billToSiteId: billTo || null, documentDate: quoteDate || null,
          lines: previewLinesOf(drafts, (d) => ({
            discountType: d.discount ? d.discount.kind : null,
            discountValue: d.discount ? (d.discount.kind === 'Percentage' ? d.discount.percent : d.discount.amount.amount / 100) : null,
          })),
        }
      : null,
    apiBaseUrl,
  );

  /**
   * 🔒 A contact added from the block (decisions 1750, 1860): the Account's list is read again, which is the only place its
   * address stands, and the new person is ticked, with an address or without one. The other fields are left as typed.
   */
  const addedContact = async (personId: string): Promise<void> => {
    if (!query) return;
    const read = await get<QuoteDefaults>(`${QUOTES_PATH}/defaults?${query}`, apiBaseUrl);
    if (!read.ok || !read.data.recipients) return;
    const fresh = read.data.recipients;
    setContacts(fresh);
    setTicked((held) => {
      const next = new Set(held);
      if (fresh.some((c) => c.personId === personId)) next.add(personId);
      return next;
    });
  };

  const save = async (then: 'view' | 'send'): Promise<void> => {
    if (needsAccount) return setError(t('quote.new.chooseAccount'));
    if (!answer) return setError(t('quote.new.readFailed'));
    if (!billTo) return setError(t('quote.new.needsBillTo'));
    if (hasUnnamedDivider(drafts)) return setError(t('item.dividerNeedsName'));
    setBusy(true);
    const header = { billToSiteId: billTo, shipToSiteId: shipTo || null, quoteDate: quoteDate || null, expiryDate: expiryDate || null, paymentTerms: terms || null, displayMode, shipmentPreferenceId: shipmentPreference || null, customerNotes, customerPoNumber: customerPo.trim() || null, ...(isService && (!quoteId || notToExceed !== openedNotToExceed) ? { notToExceedCents: notToExceed } : {}), ...(quoteId || ticked.size > 0 ? { recipientPersonIds: [...ticked] } : {}) };
    const saved = quoteId
      ? await put<{ id: string }>(`${QUOTES_PATH}/${quoteId}`, header, apiBaseUrl)
      : await post<{ id: string }>(QUOTES_PATH, { companyId: answer.companyId, dealId: answer.deal?.id ?? null, ...header }, apiBaseUrl);
    if (!saved.ok) {
      setBusy(false);
      return setError(saved.message);
    }
    const id = quoteId ?? saved.data.id;
    const lines = await put(`${QUOTES_PATH}/${id}/lines`, saveRows(), apiBaseUrl);
    setBusy(false);
    if (!lines.ok) return setError(lines.message);
    navigate(`/quotes/${id}${then === 'send' ? '?act=send' : ''}`);
  };

  return (
    <>
    <DocumentEditPage
      title={answer?.quoteNumber ?? newDocumentTitle('Quote')}
      subtitle={answer?.companyName ?? account?.label ?? ''}
      outwardLabel={t('document.edit.saveAndSend')}
      // 🔨 The footer stays usable while no Account stands behind the form, so pressing it says why rather than doing nothing.
      busy={busy}
      error={error ?? (readFailed ? t('quote.new.readFailed') : null)}
      onDismissError={() => setError(null)}
      onSaveDraft={() => void save('view')}
      onSaveAndSend={() => void save('send')}
      onCancel={() => navigate(quoteId ? `/quotes/${quoteId}` : '/quotes')}
      header={
        <>
          {asksForAccount ? (
            <div data-quote-field="account" data-quote-account={answer?.companyId ?? ''}>
              <Dropdown size={Size.Medium} label={t('quote.new.account')} entityLabel={t('quote.new.accountEntity')} search={accountSearch} value={account?.label ?? null} onSelect={(row) => setAccount({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'record', recordType: 'CompanyRecord', role: 'customer' }, (row) => setAccount({ id: row.id, label: row.line1 }))} required />
            </div>
          ) : (
            <ModalField label={t('quote.new.account')}>
              <span data-quote-account={answer?.companyId ?? ''}>{answer?.companyName ?? account?.label ?? ''}</span>
            </ModalField>
          )}
          {answer?.deal ? (
            <ModalField label={t('quote.new.deal')}>
              <span data-quote-deal={answer.deal.id}>{answer.deal.name}</span>
            </ModalField>
          ) : null}
          <div className={styles.sitePicker}>
            <SitePicker picker="billTo" label={t('quote.new.billTo')} value={billTo} onChange={setBillTo} sites={siteChoices.sites} companyId={answer?.companyId ?? null} companyName={answer?.companyName ?? null} apiBaseUrl={apiBaseUrl} required onCreated={siteAdded(setBillTo)} data={{ 'data-quote-site': 'billTo' }} />
            {answer && siteChoices.sites.length === 0 ? <p className={styles.hint} data-quote-no-sites="true">{t('quote.new.noSites')}</p> : null}
          </div>
          <div className={styles.sitePicker}>
            <SitePicker picker="shipTo" label={t('quote.new.shipTo')} value={shipTo} onChange={setShipTo} sites={siteChoices.sites} companyId={answer?.companyId ?? null} companyName={answer?.companyName ?? null} apiBaseUrl={apiBaseUrl} placeholder={t('quote.new.notChosen')} onCreated={siteAdded(setShipTo)} data={{ 'data-quote-site': 'shipTo' }} />
          </div>
          {/* 🔒 The customer's own purchase order number, beside the ship-to (T1720; decision 2290). */}
          <TextInput size={Size.Medium} label={t('quote.new.customerPo')} value={customerPo} onChange={setCustomerPo} name="quoteCustomerPo" />
          {/* 🔒 The Deal's ceiling, read and written here on a Service deal's quote (decision 2612). */}
          {isService ? <NumericInput size={Size.Medium} kind="currency" label={t('quote.notToExceed')} value={notToExceed} onChange={setNotToExceed} hint={t('quote.notToExceedHint')} name="quoteNotToExceed" /> : null}
          <DatePicker size={Size.Small} label={t('quote.new.quoteDate')} value={quoteDate} onChange={setQuoteDate} mode="date" name="quoteDate" />
          <DatePicker size={Size.Small} label={t('quote.new.expiryDate')} value={expiryDate} onChange={setExpiryDate} mode="date" name="quoteExpiryDate" />
          <TextInput size={Size.Large} label={t('quote.new.paymentTerms')} value={terms} onChange={setTerms} name="quotePaymentTerms" />
          <ShipmentPreferencePicker value={shipmentPreference} onChange={setShipmentPreference} apiBaseUrl={apiBaseUrl} name="quote" />
          <Select size={Size.Medium} label={t('quote.new.displayMode')} value={displayMode} onChange={setDisplayMode} options={displayModes.map((o) => ({ value: o.value, label: o.label }))} dataAttribute={{ 'data-quote-display-mode': 'true' }} required />
          {answer?.taxExempt ? <p className={styles.hint} data-quote-tax-exempt="true">{t('quote.new.taxExempt')}</p> : null}
          <div className={styles.wide}>
            <QuoteRecipients
              contacts={contacts}
              ticked={ticked}
              onChange={setTicked}
              // 🔒 Add new keys the new-person window to the Account and ticks the person it creates (decision 1750).
              onAddNew={() => addNew.open({ kind: 'contact', companyId: answer?.companyId ?? null, companyName: answer?.companyName ?? null, pick: 'person' }, (row) => void addedContact(row.id))}
            />
          </div>
          <CustomerNotesBox value={customerNotes} onChange={setCustomerNotes} name="quote" />
        </>
      }
      lines={
        <>
          {/* 🔨 Deal → Quote: the BOM is the starting point, never a view of the Quote's lines, and what it generates is editable (decision 0440). */}
          {dealForBom ? (
            <p className={styles.hint} data-quote-bom-action="true">
              <Button size={Size.Small} color={Color.Inactive} text={[t('quote.new.generateFromBom')]} actions={[() => void generateFromBom()]} disabled={generating} />
            </p>
          ) : null}
          <ItemTable
            documentType="Quote"
            // 🔒 Budget (hours) stands beside the quantity, drawn on a service line alone (decision 0860), and a Quote with no service line, a Parts quote among them, draws no such column (T926; decision 1713).
            columns={drafts.some((draft) => draft.service === true) ? ['item', 'qty', 'budget', 'unit', 'rate', 'account', 'tax', 'amount'] : ['item', 'qty', 'unit', 'rate', 'account', 'tax', 'amount']}
            lines={drafts}
            onChange={setDrafts}
            // 🔒 The Discount column follows the Discount Scope setting (decision 0097), which the Quote's save already enforces; the literal Footer ignored it (T122).
            discountLevel={answer?.discountLevel === 'Line' ? 'Line' : 'Footer'}
            {...pickerProps({ saleGuards: true, service: true })}
            accountSearch={lineAccountSearch}
            taxSearch={taxSearch}
            // 🔨 The figure under the category is the calculation's own, worked out live and never typed (decisions 0435, 0644).
            taxAmountCentsOf={(draft) => preview.taxCentsOf(draft.id)}
            totals={preview}
            allowances={isService}
            optionals
          />
        </>
      }
    />
    {addNew.host}
    </>
  );
}

/** The Quote's own page element: edit mode where the URL names it, the record page otherwise (decision 0409). */
export function quoteIsNew(recordId: string): boolean {
  return recordId === NEW_DOCUMENT_ID;
}
